import React, { useState } from "react";
import { Users, Shield, UserPlus, Search, XCircle, ChevronDown, ChevronUp, Music } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

import { API } from "../../App";

const KJBookingsDropdown = ({ memberId }) => {
  const [open, setOpen] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchBookings = async () => {
    if (loaded) { setOpen(!open); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/finance/kj-bookings/${memberId}`);
      setBookings(res.data?.bookings || []);
      setLoaded(true);
      setOpen(true);
    } catch { toast.error("Failed to load bookings"); }
    finally { setLoading(false); }
  };

  return (
    <div>
      <button
        onClick={fetchBookings}
        data-testid={`kj-bookings-toggle-${memberId}`}
        className="flex items-center gap-1 text-xs font-bold text-purple-700 bg-purple-50 border border-purple-200 rounded px-2 py-1 hover:bg-purple-100"
      >
        <Music size={12} />
        Bookings {loading ? '...' : `(${loaded ? bookings.length : '?'})`}
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && (
        <div className="mt-2 bg-white border-2 border-purple-200 rounded p-2 max-h-48 overflow-y-auto">
          {bookings.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-2">No bookings assigned</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-purple-50 border-b">
                  <th className="p-1 text-left">Date</th>
                  <th className="p-1 text-left">Code</th>
                  <th className="p-1 text-left">Slot</th>
                  <th className="p-1 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b, i) => (
                  <tr key={i} className="border-b last:border-0 hover:bg-gray-50">
                    <td className="p-1 font-mono">{b.date}</td>
                    <td className="p-1 font-bold">{b.booking_code}</td>
                    <td className="p-1">{b.slot_type === 'afternoon' ? '1-5PM' : '6-10PM'}</td>
                    <td className="p-1">
                      <span className={`px-1 py-0.5 rounded text-[10px] font-bold ${
                        b.status === 'completed' ? 'bg-green-100 text-green-700' :
                        b.status === 'paid' ? 'bg-blue-100 text-blue-700' :
                        b.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-600'
                      }`}>{b.status?.toUpperCase()}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

const AdminDirectoryTab = ({
  activeSubView,
  setActiveSubView,
  members,
  filteredMembers,
  memberSearchQuery,
  setMemberSearchQuery,
  showMemberForm,
  setShowMemberForm,
  memberData,
  setMemberData,
  user,
  onCreateMember,
  onEditMember,
  onDeleteMember,
  onToggleKjEligibility,
  onFetchMembers,
}) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <button
          onClick={() => { setActiveSubView(activeSubView === 'members' ? null : 'members'); onFetchMembers(); }}
          className={`p-4 border-2 border-black font-bold text-left transition-all ${
            activeSubView === 'members' ? 'bg-blue-500 text-white' : 'bg-white hover:bg-blue-50'
          }`}
        >
          <Users className="mb-2" size={24} />
          <div className="text-sm">KJ Roster</div>
          <div className="text-xs mt-1">Search & View KJs</div>
        </button>
        
        <button
          onClick={() => { setActiveSubView(activeSubView === 'admins' ? null : 'admins'); onFetchMembers(); }}
          className={`p-4 border-2 border-black font-bold text-left transition-all ${
            activeSubView === 'admins' ? 'bg-purple-500 text-white' : 'bg-white hover:bg-purple-50'
          }`}
        >
          <Shield className="mb-2" size={24} />
          <div className="text-sm">Admin Accounts</div>
          <div className="text-xs mt-1">System Administrators</div>
        </button>
        
        <button
          onClick={() => { setShowMemberForm(true); setActiveSubView('addmember'); }}
          className={`p-4 border-2 border-black font-bold text-left transition-all ${
            activeSubView === 'addmember' ? 'bg-green-500 text-white' : 'bg-white hover:bg-green-50'
          }`}
        >
          <UserPlus className="mb-2" size={24} />
          <div className="text-sm">Create Account</div>
          <div className="text-xs mt-1">Admin / KJ / Both</div>
        </button>
      </div>

      {/* KJ Roster */}
      {activeSubView === 'members' && (
        <div className="brutal-card">
          <h3 className="text-xl font-bold mb-4">KJ Roster</h3>
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search by name, email, phone..."
                className="w-full pl-10 pr-4 py-2 border-2 border-black"
                value={memberSearchQuery}
                onChange={(e) => setMemberSearchQuery(e.target.value)}
              />
            </div>
          </div>
          
          {filteredMembers.filter(m => !m.is_admin || m.is_kj).filter(m => !m.is_master_admin).length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="mx-auto mb-2 text-gray-300" size={48} />
              <p className="font-bold">No KJs Found</p>
              <p className="text-sm">Create a new KJ account to get started</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredMembers
                .filter(member => (member.is_kj !== false) && !member.is_master_admin)
                .filter(member => !member.is_admin || member.is_kj)
                .map(member => {
                  const kjType = member.kj_allowance_eligible 
                    ? { label: 'EXTERNAL KJ', color: 'bg-green-100 text-green-700' }
                    : { label: 'RN MEMBER', color: 'bg-yellow-100 text-yellow-700' };
                  
                  return (
                    <div key={member.member_id} className="border-2 border-black rounded p-3 hover:bg-gray-50">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                        <div className="flex items-center gap-3">
                          <span className="font-bold notranslate" translate="no">{member.name}</span>
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${kjType.color}`}>{kjType.label}</span>
                          {member.is_admin && <span className="px-2 py-0.5 text-xs font-bold rounded bg-purple-100 text-purple-700">ADMIN</span>}
                        </div>
                        <div className="flex gap-2 items-center">
                          <button
                            onClick={() => onToggleKjEligibility(member.member_id, member.name, member.kj_allowance_eligible)}
                            className={`px-3 py-1 text-xs font-bold rounded-full transition-all ${
                              member.kj_allowance_eligible 
                                ? 'bg-green-500 text-white hover:bg-green-600' 
                                : 'bg-yellow-400 text-yellow-900 hover:bg-yellow-500'
                            }`}
                          >
                            {member.kj_allowance_eligible ? '$35' : '$0'}
                          </button>
                          <button onClick={() => onEditMember(member)} className="px-2 py-1 text-xs font-bold bg-blue-100 text-blue-700 border border-blue-300 rounded hover:bg-blue-200">Edit</button>
                          <button onClick={() => onDeleteMember(member)} className="px-2 py-1 text-xs font-bold bg-red-100 text-red-700 border border-red-300 rounded hover:bg-red-200">Delete</button>
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mb-2">
                        <span className="notranslate" translate="no">{member.email}</span> | <span className="notranslate" translate="no">{member.phone}</span>
                      </div>
                      <KJBookingsDropdown memberId={member.member_id} />
                    </div>
                  );
                })}
            </div>
          )}
          <div className="mt-3 text-xs text-gray-500 space-y-1">
            <p><span className="px-2 py-0.5 bg-green-100 text-green-700 rounded font-bold">EXTERNAL KJ</span> = $35 Allowance per session</p>
            <p><span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded font-bold">RN MEMBER</span> = $0 Allowance (Volunteer)</p>
          </div>
        </div>
      )}
      
      {/* Admin Accounts */}
      {activeSubView === 'admins' && (
        <div className="brutal-card">
          <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
            <Shield className="text-purple-500" /> System Administrators
          </h3>
          
          {filteredMembers.filter(m => m.is_admin || m.is_master_admin).length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Shield className="mx-auto mb-2 text-gray-300" size={48} />
              <p className="font-bold">No Admin Accounts Found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="bg-purple-100 border-b-2 border-black">
                    <th className="text-left p-3 font-bold">Name</th>
                    <th className="text-left p-3 font-bold">Email</th>
                    <th className="text-left p-3 font-bold">Phone</th>
                    <th className="text-left p-3 font-bold">Role</th>
                    <th className="text-left p-3 font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers
                    .filter(member => member.is_admin || member.is_master_admin)
                    .map(member => {
                      const isMaster = member.is_master_admin;
                      const isAlsoKJ = member.is_kj;
                      const isSelf = member.member_id === user?.member_id;
                      
                      return (
                        <tr key={member.member_id} className="border-b hover:bg-purple-50">
                          <td className="p-3 font-bold notranslate" translate="no">
                            {member.name}
                            {isSelf && <span className="ml-2 text-xs text-gray-400">(You)</span>}
                          </td>
                          <td className="p-3 text-sm notranslate" translate="no">{member.email}</td>
                          <td className="p-3 text-sm notranslate" translate="no">{member.phone || '-'}</td>
                          <td className="p-3">
                            <div className="flex gap-1 flex-wrap">
                              <span className={`px-2 py-0.5 text-xs font-bold rounded ${isMaster ? 'bg-red-100 text-red-700' : 'bg-purple-100 text-purple-700'}`}>
                                {isMaster ? 'MASTER ADMIN' : 'ADMIN'}
                              </span>
                              {isAlsoKJ && <span className="px-2 py-0.5 text-xs font-bold rounded bg-green-100 text-green-700">+ KJ</span>}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-2">
                              <button
                                onClick={async () => {
                                  if (window.confirm(`Reset password for ${member.name}?`)) {
                                    try {
                                      const res = await axios.post(`${API}/admin/members/${member.member_id}/reset-password`, {}, {
                                        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                      });
                                      toast.success(res.data.message);
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Failed to reset password');
                                    }
                                  }
                                }}
                                className="px-2 py-1 text-xs font-bold bg-blue-100 text-blue-700 border border-blue-300 rounded hover:bg-blue-200"
                              >
                                Reset PW
                              </button>
                              {!isMaster && !isSelf && (
                                <button
                                  onClick={async () => {
                                    if (window.confirm(`Delete admin account "${member.name}"?\n\nThis action cannot be undone.`)) {
                                      try {
                                        await axios.delete(`${API}/admin/members/${member.member_id}`, {
                                          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
                                        });
                                        toast.success(`Account "${member.name}" deleted`);
                                        onFetchMembers();
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Failed to delete account');
                                      }
                                    }
                                  }}
                                  className="px-2 py-1 text-xs font-bold bg-red-100 text-red-700 border border-red-300 rounded hover:bg-red-200"
                                >
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Member Form */}
      {activeSubView === 'addmember' && showMemberForm && (
        <div className="brutal-card bg-green-50 border-green-300">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-green-700">Create New Account</h3>
            <button onClick={() => { setShowMemberForm(false); setActiveSubView(null); }} className="text-gray-500 hover:text-black">
              <XCircle size={24} />
            </button>
          </div>
          <form onSubmit={onCreateMember} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold mb-1">Full Name *</label>
                <input type="text" className="brutal-input w-full" value={memberData.name}
                  onChange={(e) => setMemberData({...memberData, name: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Email *</label>
                <input type="email" className="brutal-input w-full" value={memberData.email}
                  onChange={(e) => setMemberData({...memberData, email: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Phone *</label>
                <input type="tel" className="brutal-input w-full" value={memberData.phone}
                  onChange={(e) => setMemberData({...memberData, phone: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Password *</label>
                <input type="password" className="brutal-input w-full" value={memberData.password}
                  onChange={(e) => setMemberData({...memberData, password: e.target.value})} required minLength={6} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-bold mb-1">Mailing Address</label>
              <input type="text" className="brutal-input w-full" value={memberData.mailing_address}
                onChange={(e) => setMemberData({...memberData, mailing_address: e.target.value})} />
            </div>
            
            {/* Role Selection - Checkboxes */}
            <div className="p-4 bg-white border-2 border-gray-300 rounded space-y-3">
              <p className="font-bold text-sm">Account Roles:</p>
              
              <label className={`flex items-center gap-3 p-3 border-2 rounded cursor-pointer transition-all ${memberData.is_admin ? 'bg-purple-50 border-purple-400' : 'bg-white border-gray-200 hover:bg-purple-50'}`}>
                <input type="checkbox" className="w-5 h-5 accent-purple-600" checked={memberData.is_admin}
                  data-testid="role-admin-checkbox"
                  onChange={(e) => setMemberData({...memberData, is_admin: e.target.checked})} />
                <div>
                  <span className="font-bold text-purple-700">Administrator</span>
                  <p className="text-xs text-gray-500">Full backend management access</p>
                </div>
              </label>
              
              <label className={`flex items-center gap-3 p-3 border-2 rounded cursor-pointer transition-all ${memberData.is_kj ? 'bg-blue-50 border-blue-400' : 'bg-white border-gray-200 hover:bg-blue-50'}`}>
                <input type="checkbox" className="w-5 h-5 accent-blue-600" checked={memberData.is_kj}
                  data-testid="role-kj-checkbox"
                  onChange={(e) => setMemberData({...memberData, is_kj: e.target.checked})} />
                <div>
                  <span className="font-bold text-blue-700">Karaoke Jockey (KJ)</span>
                  <p className="text-xs text-gray-500">Can be assigned to karaoke sessions</p>
                </div>
              </label>
            </div>

            {/* KJ Type Selection - only when KJ role is checked */}
            {memberData.is_kj && (
              <div className="p-4 bg-gray-50 border-2 border-gray-300 rounded space-y-3">
                <p className="font-bold text-sm">KJ Type:</p>
                <div className="space-y-2">
                  <label className={`flex items-center gap-3 p-3 bg-white border-2 rounded cursor-pointer hover:bg-green-50 ${memberData.kj_allowance_eligible ? 'border-green-400' : 'border-gray-200'}`}>
                    <input type="radio" name="kj_type" className="w-5 h-5 accent-green-600"
                      checked={memberData.kj_allowance_eligible}
                      onChange={() => setMemberData({...memberData, is_rn_member: false, kj_allowance_eligible: true})} />
                    <div>
                      <span className="font-bold text-green-700">External KJ</span>
                      <p className="text-xs text-gray-500">$35 Allowance per completed session</p>
                    </div>
                  </label>
                  <label className={`flex items-center gap-3 p-3 bg-white border-2 rounded cursor-pointer hover:bg-yellow-50 ${!memberData.kj_allowance_eligible ? 'border-yellow-400' : 'border-gray-200'}`}>
                    <input type="radio" name="kj_type" className="w-5 h-5 accent-yellow-600"
                      checked={!memberData.kj_allowance_eligible}
                      onChange={() => setMemberData({...memberData, is_rn_member: true, kj_allowance_eligible: false})} />
                    <div>
                      <span className="font-bold text-yellow-700">RN Member</span>
                      <p className="text-xs text-gray-500">$0 Volunteer (No payment)</p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {memberData.is_admin && memberData.is_kj && (
              <div className="p-3 bg-blue-50 border-2 border-blue-200 rounded text-xs text-blue-700">
                <p className="font-bold">Admin + KJ Account:</p>
                <p>This account will have admin privileges AND appear in the KJ roster for session assignments.</p>
              </div>
            )}
            
            <button type="submit" data-testid="create-account-btn" className="w-full brutal-btn-primary">
              CREATE {memberData.is_admin && memberData.is_kj ? 'ADMIN + KJ' : memberData.is_admin ? 'ADMIN' : 'KJ'} ACCOUNT
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminDirectoryTab;
